import { CheckCircle, Loader2, XCircle } from 'lucide-react';

import type { SubmitStageStatus } from '@/sections/seeds/components/modals/AddSeedByTypeModal/hooks/useAddSeedSubmit';
import { SeedTypeOption } from '@/types';

interface SubmitProgressStepProps {
  seedType: SeedTypeOption | null;
  seedsStatus: SubmitStageStatus;
  credentialsStatus: SubmitStageStatus;
}

const SEED_TYPE_LABEL: Record<SeedTypeOption, string> = {
  [SeedTypeOption.DOMAIN_CIDR]: 'Asset',
  [SeedTypeOption.WEB_APPLICATION]: 'WebApplication',
  [SeedTypeOption.WEB_SERVICE]: 'WebService',
};

export function SubmitProgressStep({
  seedType,
  seedsStatus,
  credentialsStatus,
}: SubmitProgressStepProps) {
  const typeLabel = seedType ? SEED_TYPE_LABEL[seedType] : 'Seed';

  return (
    <div className="space-y-3 py-2">
      <ProgressRow
        status={seedsStatus}
        pendingLabel={`Adding ${typeLabel}(s)...`}
        successLabel={`${typeLabel}(s) added`}
        errorLabel={`Failed to add ${typeLabel}(s)`}
      />
      {credentialsStatus !== 'skipped' && (
        <ProgressRow
          status={credentialsStatus}
          pendingLabel="Adding Credentials..."
          successLabel="Credentials added"
          errorLabel="Failed to add credentials"
        />
      )}
    </div>
  );
}

interface ProgressRowProps {
  status: SubmitStageStatus;
  pendingLabel: string;
  successLabel: string;
  errorLabel: string;
}

function ProgressRow({
  status,
  pendingLabel,
  successLabel,
  errorLabel,
}: ProgressRowProps) {
  return (
    <div className="flex items-center gap-3">
      <StatusIcon status={status} />
      <span className="text-chariot-text-primary text-sm">
        {labelFor(status, pendingLabel, successLabel, errorLabel)}
      </span>
    </div>
  );
}

function StatusIcon({ status }: { status: SubmitStageStatus }) {
  switch (status) {
    case 'pending':
      return (
        <Loader2 className="text-chariot-brand-primary size-5 animate-spin" />
      );
    case 'success':
      return <CheckCircle className="text-chariot-status-success size-5" />;
    case 'error':
      return <XCircle className="text-chariot-status-critical size-5" />;
    case 'idle':
    case 'skipped':
    default:
      return (
        <div className="border-chariot-stroke size-5 rounded-full border" />
      );
  }
}

function labelFor(
  status: SubmitStageStatus,
  pendingLabel: string,
  successLabel: string,
  errorLabel: string
): string {
  switch (status) {
    case 'success':
      return successLabel;
    case 'error':
      return errorLabel;
    case 'pending':
    case 'idle':
    case 'skipped':
    default:
      return pendingLabel;
  }
}
